/**
 * Every kind of section a report can contain.
 *
 * ═════════════════════════════════════════════════════════════════════════
 * ADDING A NEW KIND
 * ═════════════════════════════════════════════════════════════════════════
 *
 * Copy the folder of the closest existing kind. `divider/` is the simplest,
 * `table/` the fullest. Then:
 *
 *   1. **Describe its shape** in `domain/section.ts`: an interface extending
 *      `SectionBase` with `kind` as a literal, and add it to the `Section`
 *      union and the `SectionKind` union. The compiler will now name every
 *      remaining step.
 *
 *   2. **Write the folder**, `sections/<kind>/`:
 *        index.ts          the definition: kind, band, icon, create, read
 *        <Kind>Block.vue   how it draws on the page
 *        <Kind>Inspector.vue   how it is edited, if it has settings
 *
 *   3. **Register it** by adding one line to `DEFINITIONS` below.
 *
 *   4. **Name it** in `i18n/fr.ts` and `i18n/en.ts`: `sections.<kind>` for the
 *      palette and outline, `newSection.<kind>` for what a new one is called on
 *      the page, `palette.hints.<kind>` for the hover, and whatever the
 *      inspector says under `inspector.<kind>`.
 *
 * That is the whole list. Nothing else mentions a kind by name: the palette,
 * the outline, the canvas, the inspector, the reader, the drag layer and both
 * exporters all work from this registry.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Two things worth knowing while writing one:
 *
 * **`read` is forgiving by contract.** It is handed whatever was stored,
 * which may have been written by an earlier version, by an import, or by
 * something other than this application. Every field falls back on its own.
 * Refusing to open a report because one field is the wrong type loses
 * somebody's work to protect a type.
 *
 * **`create` is handed its title already translated.** A section's title is
 * content: it is written into the document, printed, and edited by hand, so it
 * has to be in the language the person is working in. The definition never
 * invents one.
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { Component } from 'vue'

import type { JsonValue } from '@/domain/json'
import type { Section, SectionKind } from '@/domain/section'
import { chartSection } from '@/sections/chart'
import { contentsSection } from '@/sections/contents'
import { datapointSection } from '@/sections/datapoint'
import { coverSection } from '@/sections/cover'
import { disclosureIndexSection } from '@/sections/disclosureIndex'
import { dividerSection } from '@/sections/divider'
import { ghgSection } from '@/sections/ghg'
import { glossarySection } from '@/sections/glossary'
import { groupSection } from '@/sections/group'
import { headingSection } from '@/sections/heading'
import { imageSection } from '@/sections/image'
import { imagePageSection } from '@/sections/imagePage'
import { fixedPageSection } from '@/sections/fixedPage'
import { iroSection } from '@/sections/iro'
import { kpiSection } from '@/sections/kpi'
import { layerSection } from '@/sections/layer'
import { materialitySection } from '@/sections/materiality'
import { noteSection } from '@/sections/note'
import { pageBreakSection } from '@/sections/pageBreak'
import { risksSection } from '@/sections/risks'
import { scopeSection } from '@/sections/scope'
import { signaturesSection } from '@/sections/signatures'
import { tableSection } from '@/sections/table'
import { targetSection } from '@/sections/target'
import { taxonomySection } from '@/sections/taxonomy'
import { textSection } from '@/sections/text'
import type { AnySectionDefinition, PaletteBand, ReadContext } from '@/sections/types'

export type { PaletteBand, ReadContext, SectionDefinition } from '@/sections/types'

/**
 * The registry.
 *
 * Order is not significant: the palette groups by band and the outline follows
 * the document, so nothing reads this as a sequence.
 */
const DEFINITIONS: readonly AnySectionDefinition[] = [
  coverSection,
  groupSection,
  headingSection,
  textSection,
  tableSection,
  imageSection,
  chartSection,
  kpiSection,
  noteSection,
  scopeSection,
  glossarySection,
  datapointSection,
  targetSection,
  materialitySection,
  iroSection,
  risksSection,
  ghgSection,
  taxonomySection,
  disclosureIndexSection,
  contentsSection,
  signaturesSection,
  dividerSection,
  pageBreakSection,
  layerSection,
  imagePageSection,
  fixedPageSection,
] as AnySectionDefinition[]

const BY_KIND = new Map(DEFINITIONS.map((definition) => [definition.kind, definition]))

/** Every kind, for anything that has to offer or validate all of them. */
export const SECTION_KINDS: readonly SectionKind[] = DEFINITIONS.map(
  (definition) => definition.kind,
)

const KIND_SET: ReadonlySet<string> = new Set<string>(SECTION_KINDS)

/** Whether a stored string names a kind this version knows. */
export function isSectionKind(value: string): value is SectionKind {
  return KIND_SET.has(value)
}

/**
 * The definition for a kind.
 *
 * Throws rather than falling back, and deliberately: reaching this with an
 * unknown kind means something bypassed `isSectionKind`, which is a programming
 * mistake and not a document problem. Documents are handled by the reader,
 * which drops what it cannot recognise.
 */
export function definitionFor(kind: SectionKind): AnySectionDefinition {
  const definition = BY_KIND.get(kind)
  if (!definition) throw new Error(`No section definition is registered for '${kind}'`)
  return definition
}

/** The kinds in one band of the palette, in the order they were registered. */
export function kindsInBand(band: PaletteBand): readonly SectionKind[] {
  return DEFINITIONS.filter(
    (definition) => definition.band === band && definition.inPalette !== false,
  ).map((definition) => definition.kind)
}

/** The bands, in the order they should be offered. */
export const PALETTE_BANDS: readonly PaletteBand[] = [
  'structure',
  'content',
  'annex',
  'sustainability',
  'frontAndBack',
]

/** The icon paths for a kind, on a 16-unit grid. */
export function iconFor(kind: SectionKind): readonly string[] {
  return BY_KIND.get(kind)?.icon ?? []
}

/** How a kind draws on the page. */
export function blockFor(kind: SectionKind): Component {
  return definitionFor(kind).block
}

/** How a kind is edited, or nothing when it is edited on the page. */
export function inspectorFor(kind: SectionKind): Component | null {
  return BY_KIND.get(kind)?.inspector ?? null
}

/** Whether a kind holds other sections. Only a group does. */
export function holdsChildren(kind: SectionKind): boolean {
  return BY_KIND.get(kind)?.holdsChildren === true
}

/** A new section of a kind, with a title the caller has already translated. */
export function createSectionOfKind(kind: SectionKind, id: string, title: string): Section {
  return definitionFor(kind).create(id, title)
}

/** One section read back off a stored document. */
export function readSectionOfKind(kind: SectionKind, context: ReadContext): Section {
  return definitionFor(kind).read(context)
}

/** What a section is stored as. Kinds have no say in this: it is their fields. */
export type StoredSection = Readonly<Record<string, JsonValue>>
